/**
 * Logo — the ONE live render of the Coravu sun mark + COR·A·VU wordmark.
 *
 * Every shell topbar, marketing nav/footer, auth card, and loading screen
 * renders through this component instead of re-implementing the SVG inline.
 * Geometry comes from `MARK_GEOMETRY` in `lib/logos.ts` — the SAME source
 * the `/brand` LogoOverlay and the downloadable SVG/PNG assets derive from —
 * so the reference page and the live app can never drift.
 *
 * Not theme-keyed: colors are explicit props (defaulted from brand tokens by
 * the registry entry's background), because the mark is tinted per-surface
 * (theme sage/amber on dark chrome, forest ring on light). Never hardcode a
 * hex here — pull from COLORS / FONTS or take it as a prop.
 *
 * - `<Logo id="mark-on-dark" size={28} tile={theme.surfaceRaisedBg} />` — mark
 * - `<Logo id="wordmark-on-dark" fontSize={17} letterSpacing={3} />` — wordmark
 * - `<Logo id="lockup-on-dark" size={28} />` — mark + wordmark inline
 *
 * For ANIMATED marks (brand entrances/exits, cursor follower) import
 * `MARK_GEOMETRY` directly and drive your own Animated.Circle from the shared
 * radii — same source, animation preserved.
 */
import { Text, View, type StyleProp, type TextStyle, type ViewStyle } from "react-native";
import Svg, { Circle, Line, Rect } from "react-native-svg";

import { COLORS, FONTS } from "../../lib/brand";
import { LOGOS, MARK_GEOMETRY } from "../../lib/logos";

/* ─────────────────────── Sun mark ─────────────────────── */

export type LogoMarkProps = {
  /** Square render size in px. */
  size?: number;
  /** Outer ring + ray stroke. Default brand sage. */
  ringOuter?: string;
  /** Inner ring stroke. Default brand amber. */
  ringInner?: string;
  /** Center dot fill. Default brand amber. */
  dot?: string;
  /** Rounded chrome tile behind the mark (shell topbars). Omit for a bare mark. */
  tile?: string;
  /**
   * Ray treatment:
   * - "cardinal" (default): 4 rays — shells, footer, auth.
   * - "full": 8 rays (adds diagonals) — marketing nav sunburst.
   * - "none": rings + dot only — student shell tile.
   */
  rays?: "none" | "cardinal" | "full";
  style?: StyleProp<ViewStyle>;
};

export function LogoMark({
  size = MARK_GEOMETRY.viewBox,
  ringOuter = COLORS.SAGE.hex,
  ringInner = COLORS.AMBER.hex,
  dot = COLORS.AMBER.hex,
  tile,
  rays = "cardinal",
  style,
}: LogoMarkProps) {
  const g = MARK_GEOMETRY;
  const c = g.center;
  return (
    <Svg width={size} height={size} viewBox={`0 0 ${g.viewBox} ${g.viewBox}`} fill="none" style={style}>
      {tile ? <Rect width={g.viewBox} height={g.viewBox} rx={g.tileRadius} fill={tile} /> : null}
      <Circle cx={c} cy={c} r={g.ringOuter.r} stroke={ringOuter} strokeWidth={g.ringOuter.strokeWidth} fill="none" />
      <Circle cx={c} cy={c} r={g.ringInner.r} stroke={ringInner} strokeWidth={g.ringInner.strokeWidth} fill="none" />
      <Circle cx={c} cy={c} r={g.dot.r} fill={dot} />
      {rays !== "none"
        ? g.ray.lines.map(([x1, y1, x2, y2], i) => (
            <Line key={`c${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={ringOuter} strokeWidth={g.ray.strokeWidth} />
          ))
        : null}
      {rays === "full"
        ? g.rayDiagonal.lines.map(([x1, y1, x2, y2], i) => (
            <Line key={`d${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={ringOuter} strokeWidth={g.rayDiagonal.strokeWidth} />
          ))
        : null}
    </Svg>
  );
}

/* ─────────────────────── Wordmark ─────────────────────── */

export type LogoWordmarkProps = {
  /** Letter color (the COR / VU). Default brand linen. */
  color?: string;
  /** Accent color for the central A. Default brand amber. */
  accent?: string;
  fontSize?: number;
  letterSpacing?: number;
  /** Default Cormorant semibold — matches every shell wordmark. */
  fontFamily?: string;
  style?: StyleProp<TextStyle>;
  numberOfLines?: number;
  /** NativeWind classes for sites that size/space the wordmark via Tailwind
   *  (e.g. auth card, student shell). Inline style still applies on top. */
  className?: string;
};

export function LogoWordmark({
  color = COLORS.LINEN.hex,
  accent = COLORS.AMBER.hex,
  fontSize,
  letterSpacing,
  fontFamily = FONTS.displaySemiBold,
  style,
  numberOfLines,
  className,
}: LogoWordmarkProps) {
  return (
    <Text
      className={className}
      numberOfLines={numberOfLines}
      style={[{ fontFamily, color }, fontSize != null ? { fontSize } : null, letterSpacing != null ? { letterSpacing } : null, style]}
    >
      COR<Text style={{ color: accent }}>A</Text>VU
    </Text>
  );
}

/* ─────────────────────── Registry-keyed convenience ─────────────────────── */

export type LogoProps = LogoMarkProps &
  Omit<LogoWordmarkProps, "style"> & {
    /** Registry id from lib/logos.ts — selects mark vs wordmark vs lockup and the on-dark/on-light defaults. */
    id: string;
    markStyle?: StyleProp<ViewStyle>;
    wordmarkStyle?: StyleProp<TextStyle>;
    /** Gap between mark and wordmark in a lockup. */
    gap?: number;
  };

/**
 * `<Logo id="…" />` — looks up the registry entry, picks the right render
 * (mark / wordmark / lockup) and applies on-light vs on-dark color defaults.
 * Any color/size/font prop overrides the default for per-surface tinting.
 */
export function Logo({ id, markStyle, wordmarkStyle, gap = 10, ...props }: LogoProps) {
  const entry = LOGOS.find((l) => l.id === id);
  const onLight = entry?.background === "light";
  // On light surfaces the outer ring is forest-green for contrast; on dark it's sage.
  const ringOuter = props.ringOuter ?? (onLight ? COLORS.SAGE_DEEP.hex : COLORS.SAGE.hex);
  const ringInner = props.ringInner ?? COLORS.AMBER.hex;
  const dot = props.dot ?? COLORS.AMBER.hex;
  const textColor = props.color ?? (onLight ? COLORS.FOREST.hex : COLORS.LINEN.hex);
  const accent = props.accent ?? COLORS.AMBER.hex;

  const mark = (
    <LogoMark
      size={props.size}
      ringOuter={ringOuter}
      ringInner={ringInner}
      dot={dot}
      tile={props.tile}
      rays={props.rays}
      style={markStyle}
    />
  );
  const wordmark = (
    <LogoWordmark
      color={textColor}
      accent={accent}
      fontSize={props.fontSize}
      letterSpacing={props.letterSpacing}
      fontFamily={props.fontFamily}
      style={wordmarkStyle}
    />
  );

  const category = entry?.category;
  if (category === "Mark" || category === "Favicon" || category === "App Icon") return mark;
  if (category === "Wordmark" && entry?.id.startsWith("lockup")) {
    return (
      <View style={{ flexDirection: "row", alignItems: "center", gap }}>
        {mark}
        {wordmark}
      </View>
    );
  }
  if (category === "Wordmark" || category === "Email") return wordmark;
  // Unknown id → safe default: bare wordmark.
  return wordmark;
}
